import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { NavItem, NavItemActions } from '@rockwell-automation-inc/common-utils';
import { PermissionCondition } from '@servicesV2/permissions.service';

export class ShellNavigationConstants {
  static readonly HUBMANAGER_SETTINGS_LINK_ID = 'settings';
  static readonly HUBMANAGER_LEDGER_LINK_ID = 'ledger';
  static readonly HUBMANAGER_ACCESS_MANAGEMENT_LINK_ID = 'access-management';
  static readonly HUBMANAGER_ACCESS_MANAGEMENT_LINK_ROUTE = 'access-mngmt';
  static readonly HUBMANAGER_VIEW_ORG_ENTITLEMENTS_LINK_ID = 'org-entitlements';
  static readonly HUBMANAGER_VIEW_ORG_ENTITLEMENTS_LINK_ROUTE = 'entitlement';
  static readonly HUBMANAGER_VIEW_MY_ENTITLEMENTS_LINK_ID = 'my-entitlements';
  static readonly HUBMANAGER_VIEW_MY_ENTITLEMENTS_LINK_ROUTE =
    ShellNavigationConstants.HUBMANAGER_VIEW_MY_ENTITLEMENTS_LINK_ID;
  static readonly HUBMANAGER_INVITE_USERS_LINK_ID = 'invite-users';
  static readonly HUBMANAGER_INVITE_USERS_LINK_ROUTE = ShellNavigationConstants.HUBMANAGER_INVITE_USERS_LINK_ID;
  static readonly HUBMANAGER_APPROVE_USERS_LINK_ID = 'approve-users';
  static readonly HUBMANAGER_APPROVE_USERS_LINK_ROUTE = ShellNavigationConstants.HUBMANAGER_APPROVE_USERS_LINK_ID;
  static readonly HUBMANAGER_SETTINGS_LINK_ROUTE = ShellNavigationConstants.HUBMANAGER_SETTINGS_LINK_ID;
  static readonly SUB_ORGANIZATION_LINK_ID = 'sub-organization';
}

/**
 * Navigation item configuration with permission conditions
 */
export type NavItemConfig = {
  item: NavItem;
  condition?: PermissionCondition;
};

/**
 * Organization links configuration - dynamically generated based on tenant context
 * These links are shown when a tenant is selected
 */
export class OrganizationLinksConfig {
  static readonly BASE_LINKS: NavItemConfig[] = [
    {
      item: {
        action: NavItemActions.self,
        displayName: marker('fthub.applications'),
        id: 'applications',
        route: 'dashboard',
      },
    },
    {
      item: {
        action: 'self',
        displayName: marker('fthub.sub_organizations'),
        id: ShellNavigationConstants.SUB_ORGANIZATION_LINK_ID,
        route: 'suborgs',
      },
    },
    {
      item: {
        action: NavItemActions.self,
        displayName: marker('fthub.org_entitlements'),
        id: ShellNavigationConstants.HUBMANAGER_VIEW_ORG_ENTITLEMENTS_LINK_ID,
        route: ShellNavigationConstants.HUBMANAGER_VIEW_ORG_ENTITLEMENTS_LINK_ROUTE,
      },
    },
    {
      item: {
        action: NavItemActions.self,
        displayName: marker('fthub.manage_user_access'),
        id: ShellNavigationConstants.HUBMANAGER_ACCESS_MANAGEMENT_LINK_ID,
        route: ShellNavigationConstants.HUBMANAGER_ACCESS_MANAGEMENT_LINK_ROUTE,
      },
      condition: { permissions: ['canManageAccess'] },
    },
    {
      item: {
        action: NavItemActions.self,
        displayName: marker('fthub.send_invites'),
        id: ShellNavigationConstants.HUBMANAGER_INVITE_USERS_LINK_ID,
        route: ShellNavigationConstants.HUBMANAGER_INVITE_USERS_LINK_ROUTE,
      },
      condition: { permissions: ['canInvite'] },
    },
    {
      item: {
        action: NavItemActions.self,
        displayName: marker('fthub.approve_users'),
        id: ShellNavigationConstants.HUBMANAGER_APPROVE_USERS_LINK_ID,
        route: ShellNavigationConstants.HUBMANAGER_APPROVE_USERS_LINK_ROUTE,
      },
      condition: { permissions: ['canApproveAccess'] },
    },
    {
      item: {
        action: NavItemActions.self,
        displayName: marker('fthub.settings'),
        id: ShellNavigationConstants.HUBMANAGER_SETTINGS_LINK_ID,
        route: ShellNavigationConstants.HUBMANAGER_SETTINGS_LINK_ROUTE,
      },
      condition: { permissions: ['canEditTenantSettings'] },
    },
  ];
}

/**
 * Help menu links configuration
 */
export class HelpLinksConfig {
  static readonly ICON_OPEN = 'ra-icon-ide-md-open';
  static readonly ICON_FEEDBACK = 'ra-icon-ide-lg-feedback';
  static readonly ICON_ALARM = 'ra-icon-ide-sm-alarm';
  static readonly ICON_CONFIG = 'ra-icon-ide-sm-controller-configuration';

  static readonly BASE_LINKS: NavItem[] = [
    {
      action: NavItemActions.blank,
      raIconClass: HelpLinksConfig.ICON_OPEN,
      displayName: marker('fthub.online_help'),
      id: 'online-help',
      route: 'https://www.rockwellautomation.com/docs/en/factorytalk-hub/current/factorytalk_hub-ditamap.html',
    },
    {
      action: NavItemActions.blank,
      raIconClass: HelpLinksConfig.ICON_OPEN,
      displayName: marker('fthub.tour'),
      id: 'fthub-tour',
      route: 'https://rockwellautomation.storylane.io/share/cpq3be8iji67',
    },
    {
      action: NavItemActions.blank,
      raIconClass: HelpLinksConfig.ICON_OPEN,
      displayName: marker('fthub.getting_started'),
      id: 'getting-started',
      route:
        'https://www.rockwellautomation.com/docs/en/factorytalk-hub/current/factorytalk_hub-ditamap/ftcs_getting_started.html',
    },
    {
      action: NavItemActions.blank,
      raIconClass: HelpLinksConfig.ICON_OPEN,
      displayName: marker('fthub.search_knowledgebase'),
      id: 'search-knowledgebase',
      route:
        'https://support.rockwellautomation.com/app/home',
    },
    {
      action: 'custom',
      raIconClass: HelpLinksConfig.ICON_OPEN,
      displayName: marker('fthub.contact_support'),
      id: 'contact-support',
    },
    {
      action: 'custom',
      raIconClass: HelpLinksConfig.ICON_FEEDBACK,
      displayName: marker('fthub.provide_feedback'),
      id: 'provide-feedback',
    },
    {
      action: NavItemActions.blank,
      raIconClass: HelpLinksConfig.ICON_OPEN,
      displayName: marker('fthub.release_notes'),
      id: 'release-notes',
      route:
        'https://www.rockwellautomation.com/en-us/docs/factorytalk-hub/current/factorytalk_hub-ditamap/rel_notes.html',
    },
    {
      children: [
        {
          action: NavItemActions.blank,
          raIconClass: HelpLinksConfig.ICON_CONFIG,
          displayName: marker('fthub.build_information'),
          id: 'build-information',
        },
        {
          action: NavItemActions.blank,
          raIconClass: HelpLinksConfig.ICON_OPEN,
          displayName: marker('fthub.legal'),
          id: 'legal',
          route: 'https://www.rockwellautomation.com/en-us/company/about-us/legal-notices.html',
        },
        {
          action: NavItemActions.blank,
          raIconClass: HelpLinksConfig.ICON_OPEN,
          displayName: marker('fthub.license_agreement'),
          id: 'license-agreement',
          route:
            'https://www.rockwellautomation.com/en-us/company/about-us/legal-notices/software-cloud-services-agreement.html',
        },
        {
          action: NavItemActions.blank,
          raIconClass: HelpLinksConfig.ICON_OPEN,
          displayName: marker('fthub.privacy_policy'),
          id: 'privacy-policy',
          route:
            'https://www.rockwellautomation.com/en-us/company/about-us/legal-notices/privacy-and-cookiespolicy.html',
        },
        {
          action: NavItemActions.blank,
          raIconClass: HelpLinksConfig.ICON_OPEN,
          displayName: marker('fthub.terms_of_use'),
          id: 'terms-of-use',
          route:
            'https://www.rockwellautomation.com/en-us/company/about-us/legal-notices/terms-and-conditionsofaccess.html',
        },
      ],
      displayName: marker('fthub.about'),
      expander: true,
      id: 'about',
    },
  ];
}

/**
 * User section links configuration
 * Dynamically generated based on current user context
 */
export class UserSectionLinksConfig {
  static readonly MY_ENTITLEMENTS_LINK: NavItem = {
    action: NavItemActions.self,
    displayName: marker('fthub.my_entitlements'),
    id: ShellNavigationConstants.HUBMANAGER_VIEW_MY_ENTITLEMENTS_LINK_ID,
    route: ShellNavigationConstants.HUBMANAGER_VIEW_MY_ENTITLEMENTS_LINK_ROUTE,
  };

  static readonly ORG_ENTITLEMENTS_LINK: NavItem = {
    action: NavItemActions.self,
    displayName: marker('fthub.org_entitlements'),
    id: ShellNavigationConstants.HUBMANAGER_VIEW_ORG_ENTITLEMENTS_LINK_ID,
    route: ShellNavigationConstants.HUBMANAGER_VIEW_ORG_ENTITLEMENTS_LINK_ROUTE,
  };

  static readonly ORGANIZATIONS_LINK: NavItem = {
    action: NavItemActions.self,
    displayName: marker('fthub.organization'),
    id: 'organizations',
    route: 'organizations',
  };

  static readonly PLACEHOLDER_ORGANIZATIONS_LINK: NavItem = {
    isImage: true,
    displayName: 'Organization Placeholder',
    id: 'org-placeholder',
    imageData: 'Open an organization from the list to see menu options',
    imagePath: '/assets/images/left-menu-org-empty-state.svg',
  };
}
